import { useState, useEffect, useMemo } from 'react';
import { useLoaderData, useLocation, useParams } from 'react-router-dom';

import { LessonLoaderProps } from 'src/pages/LessonPage/loader';
import { TOP_NAVBAR_HEIGHT } from 'src/pages/LessonPage/children/Sidebar';

import { cleanupObserver } from './useIntersectionObserverForProgressTracking';

export const useIntersectionObserverForSectionHighlight = (): string | undefined => {
  const { course } = useLoaderData() as LessonLoaderProps;
  const location = useLocation();
  const params = useParams();

  const lessonSlug = params.lessonId;
  const [selectedSectionId, setSelectedSectionId] = useState<string>();
  const [visibleSections, setVisibleSections] = useState<Map<string, number>>(new Map());

  const sectionIds = useMemo(
    () =>
      course?.lessonsCollection.items.flatMap((lesson) =>
        lesson.sectionListCollection.items.map((section) => section.sys.id),
      ),
    [course],
  );

  useEffect(() => {
    const seenSectionObserver: IntersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const currentSectionId = entry.target.id;

          setVisibleSections((prev) => {
            const newMap = new Map(prev);

            if (entry.isIntersecting) {
              newMap.set(currentSectionId, entry.intersectionRatio);
            } else {
              newMap.delete(currentSectionId);
            }

            return newMap;
          });
        });

        // Find the section with the highest intersection ratio
        let maxRatio = 0;
        let maxSectionId: string | undefined;

        visibleSections.forEach((ratio, sectionId) => {
          if (ratio > maxRatio) {
            maxRatio = ratio;
            maxSectionId = sectionId;
          }
        });

        if (maxSectionId) {
          setSelectedSectionId(maxSectionId);
        }
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: `${TOP_NAVBAR_HEIGHT}px 0px 0px 0px`,
      },
    );

    if (!sectionIds) {
      return;
    }

    for (const section of [...sectionIds, 'quiz']) {
      const element = document.getElementById(section);

      if (element) {
        seenSectionObserver.observe(element);
      }
    }

    return () => cleanupObserver(sectionIds, seenSectionObserver);
  }, [sectionIds, lessonSlug, location.hash, visibleSections]);

  return selectedSectionId;
};
